import { useState } from "react";
import { Loader2, PauseCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { forgetMemberEmail } from "@/lib/memberEmail";

/** Cancel-or-pause block at the foot of the member page (active members; a referral free month doesn't block cancelling). */
export function MembershipCancelBlock({ m, token, onChange }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const rewardPause = m.pause?.reason === "referral_reward";
  if (m.status !== "active" || (m.pause && !rewardPause)) return null;

  const act = async (path, ok, fail, after) => {
    setBusy(true);
    try { const r = await api.post(`/memberships/public/${token}/${path}`); onChange(r.data); setConfirm(false); after?.(r.data); toast.success(ok); }
    catch (err) { toast.error(err?.response?.data?.detail || fail); }
    finally { setBusy(false); }
  };
  const cancel = () => act("cancel", "Membership cancelled — no more charges", "Couldn't cancel — call 310-422-0669", (d) => { if (d.status === "cancelled") forgetMemberEmail(); });
  const pause = () => act("pause", "Paused for a month — we'll see you soon", "Couldn't pause — call 310-422-0669");

  return (
    <div className="mt-8 border-t border-slate-800 pt-5 text-sm">
      {!confirm ? (
        <button type="button" onClick={() => setConfirm(true)} className="inline-flex items-center min-h-[44px] py-2 text-slate-400 hover:text-rose-300 transition-colors" data-testid="membership-cancel-btn">Cancel membership</button>
      ) : (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4" data-testid="membership-cancel-confirm">
          <p className="text-rose-100 font-semibold">{rewardPause ? "Sure you want to cancel?" : "Before you go — need a break instead?"}</p>
          <p className="text-rose-100/80 mt-1">{rewardPause
            ? "You keep your perks until the end of the month you've paid for, then billing stops for good — you'd give up the rest of your referral free month. No refunds for a partial month."
            : "Pause for a month: Stripe skips your next charge, perks pause too, and everything comes back automatically the month after. Or cancel for good — you keep your perks until the end of the month you've paid for, then billing stops. No refunds for a partial month."}</p>
          <div className="mt-3 flex flex-wrap gap-3">
            {!rewardPause && <button type="button" onClick={pause} disabled={busy} className="dg-btn-primary rounded-full px-4 py-2.5 min-h-[44px] text-sm disabled:opacity-50 flex items-center gap-2" data-testid="membership-pause-btn">{busy && <Loader2 className="w-4 h-4 animate-spin" />} <PauseCircle className="w-4 h-4" /> Pause a month instead</button>}
            <button type="button" onClick={cancel} disabled={busy} className="rounded-full px-4 py-2.5 min-h-[44px] bg-rose-500 text-white text-sm disabled:opacity-50 flex items-center gap-2" data-testid="membership-cancel-confirm-btn">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Yes, cancel</button>
            <button type="button" onClick={() => setConfirm(false)} className="dg-btn-ghost rounded-full px-4 py-2.5 min-h-[44px] text-slate-200 text-sm" data-testid="membership-keep-btn">Keep it</button>
          </div>
        </div>
      )}
    </div>
  );
}
